import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Footer from '../../components/Footer';
import { Camera, Save, X, Edit, User, Mail, Phone, MapPin, Calendar, Shield, Bell, Trash2, LayoutDashboard, LogOut } from 'lucide-react';
import ImageWithFallback from '../../components/ImageWithFallback';
import { getUserProfile, updateUserProfile, getUserEnrollments } from '../../services/api';

const Profile = () => {
    const { user, isLoggedIn, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [activeTab, setActiveTab] = useState('profile'); // profile, settings, security
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        bio: '',
        image: '',
        location: '',
        dateOfBirth: ''
    });
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [loading, setLoading] = useState(false);
    const [enrollments, setEnrollments] = useState([]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
    };

    useEffect(() => {
        if (isLoggedIn === null) return;
        if (!isLoggedIn) {
            navigate('/');
            return;
        }

        const fetchProfile = async () => {
            try {
                const res = await getUserProfile();
                if (res.success) {
                    const data = res.data;
                    setFormData({
                        name: data.name || '',
                        email: data.email || '',
                        phone: data.phone || '',
                        bio: data.bio || '',
                        image: data.image || '',
                        location: data.location || '',
                        dateOfBirth: data.dateOfBirth || ''
                    });
                    updateUser(data);
                }

                const enrollRes = await getUserEnrollments();
                if (enrollRes.success) {
                    setEnrollments(enrollRes.data);
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
                setFormData({
                    name: user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || '',
                    bio: user?.bio || '',
                    image: user?.image || '',
                    location: user?.location || '',
                    dateOfBirth: user?.dateOfBirth || ''
                });
            }
        };

        fetchProfile();
    }, [isLoggedIn, navigate]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (formData.phone && !/^\+?[\d\s-()]+$/.test(formData.phone)) {
            newErrors.phone = 'Phone number is invalid';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('Image size should be less than 5MB', 'error');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                setFormData({
                    ...formData,
                    image: e.target.result
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const triggerFileInput = () => {
        document.getElementById('profile-image-input').click();
    };

    const handleSaveProfile = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const res = await updateUserProfile(formData);
            if (res.success) {
                updateUser(formData);
                setIsEditing(false);
                showToast('Profile updated successfully!', 'success');
            }
        } catch (error) {
            showToast(error.message || 'Failed to update profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = () => {
        logout();
        navigate('/');
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const stats = {
        coursesEnrolled: enrollments.length,
        coursesCompleted: enrollments.filter(c => Number(c.progress || 0) >= 100).length,
        totalHours: enrollments.length * 5,
        certificates: enrollments.filter(c => Number(c.progress || 0) >= 99).length
    };

    return (
        <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans">
            {/* Toast Notification */}
            {toast.show && (
                <div className="fixed top-14 right-2 left-2 sm:left-auto z-[100] animate-slideIn">
                    <div className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg shadow-xl border-l-4 sm:min-w-[260px] ${toast.type === 'success'
                        ? 'bg-white border-emerald-500 text-emerald-900'
                        : 'bg-white border-red-500 text-red-900'
                        }`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${toast.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                            }`}>
                            {toast.type === 'success' ? <Save size={14} /> : <X size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className={`text-[11px] font-bold ${toast.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                                {toast.type === 'success' ? 'Success' : 'Error'}
                            </h4>
                            <p className="text-gray-600 text-[10px] truncate font-medium">{toast.message}</p>
                        </div>
                        <button
                            onClick={() => setToast({ ...toast, show: false })}
                            className="text-gray-400 hover:text-gray-600 p-0.5"
                        >
                            <X size={12} />
                        </button>
                    </div>
                </div>
            )}

            <main className="flex-1 max-w-7xl mx-auto px-2.5 sm:px-6 lg:px-8 py-3 sm:py-6 w-full space-y-3 sm:space-y-5">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5 sm:gap-6">

                    {/* Left Sidebar - Profile Summary Card */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xs border border-gray-100 overflow-hidden">
                            {/* Cover Image Banner (Proper Height for Mobile) */}
                            <div className="h-20 sm:h-28 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 relative"></div>

                            {/* Avatar & Profile Info */}
                            <div className="relative px-3 sm:px-5 pb-3.5 sm:pb-5 text-center">
                                <div className="relative -mt-9 sm:-mt-12 mb-2 inline-block">
                                    <ImageWithFallback
                                        src={formData.image}
                                        alt="Profile"
                                        fallbackSrc="/img/course-placeholder.png"
                                        className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-white shadow-xs mx-auto bg-white"
                                    />
                                    {isEditing && (
                                        <button
                                            onClick={triggerFileInput}
                                            className="absolute bottom-0 right-0 bg-emerald-600 text-white p-1 rounded-full hover:bg-emerald-700 shadow-xs transition"
                                        >
                                            <Camera size={11} />
                                        </button>
                                    )}
                                    <input
                                        type="file"
                                        id="profile-image-input"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                    />
                                </div>

                                <div className="mb-2.5">
                                    <h2 className="text-xs sm:text-base font-bold text-gray-900 truncate">{formData.name || 'Student Account'}</h2>
                                    <p className="text-[10px] text-gray-500 truncate">{formData.email}</p>
                                </div>

                                {/* 2x2 Compact Mobile Stats Grid */}
                                <div className="grid grid-cols-2 gap-1.5 sm:gap-2.5 mb-3">
                                    <div className="bg-emerald-50/80 border border-emerald-100/80 rounded-lg p-1.5 text-center">
                                        <p className="text-xs sm:text-base font-extrabold text-emerald-700">{stats.coursesEnrolled}</p>
                                        <p className="text-[8px] sm:text-[9px] text-gray-600 font-bold uppercase tracking-wider">Courses</p>
                                    </div>
                                    <div className="bg-blue-50/80 border border-blue-100/80 rounded-lg p-1.5 text-center">
                                        <p className="text-xs sm:text-base font-extrabold text-blue-700">{stats.certificates}</p>
                                        <p className="text-[8px] sm:text-[9px] text-gray-600 font-bold uppercase tracking-wider">Certificates</p>
                                    </div>
                                    <div className="bg-purple-50/80 border border-purple-100/80 rounded-lg p-1.5 text-center">
                                        <p className="text-xs sm:text-base font-extrabold text-purple-700">{stats.totalHours}h</p>
                                        <p className="text-[8px] sm:text-[9px] text-gray-600 font-bold uppercase tracking-wider">Learning</p>
                                    </div>
                                    <div className="bg-amber-50/80 border border-amber-100/80 rounded-lg p-1.5 text-center">
                                        <p className="text-xs sm:text-base font-extrabold text-amber-700">{stats.coursesCompleted}</p>
                                        <p className="text-[8px] sm:text-[9px] text-gray-600 font-bold uppercase tracking-wider">Completed</p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="space-y-1.5">
                                    <button
                                        onClick={() => navigate('/student/dashboard')}
                                        className="w-full bg-emerald-600 text-white py-1.5 rounded-lg text-xs font-bold hover:bg-emerald-700 transition flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer"
                                    >
                                        <LayoutDashboard size={13} /> View Dashboard
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full bg-gray-100 text-gray-700 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-200 transition flex items-center justify-center gap-1.5 cursor-pointer"
                                    >
                                        <LogOut size={13} /> Logout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Content Area */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xs border border-gray-100 p-2.5 sm:p-5">
                            
                            {/* Mobile Scrollable Tab Bar */}
                            <div className="flex gap-1 mb-2.5 border-b border-gray-200 pb-2 overflow-x-auto hide-scrollbar">
                                {[
                                    { id: 'profile', label: 'Profile Information', icon: User },
                                    { id: 'settings', label: 'Notifications', icon: Bell },
                                    { id: 'security', label: 'Security & Password', icon: Shield }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold transition shrink-0 cursor-pointer ${activeTab === tab.id
                                            ? 'bg-emerald-600 text-white shadow-2xs'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        <tab.icon size={12} />
                                        <span>{tab.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Profile Information Tab */}
                            {activeTab === 'profile' && (
                                <div className="space-y-2.5">
                                    <div className="flex flex-row items-center justify-between gap-2 border-b border-gray-100 pb-2">
                                        <div>
                                            <h2 className="text-xs sm:text-sm font-bold text-gray-900">Personal Information</h2>
                                            <p className="text-[9px] text-gray-500">Manage your profile details and preferences</p>
                                        </div>
                                        {!isEditing ? (
                                            <button
                                                onClick={() => setIsEditing(true)}
                                                className="bg-emerald-600 text-white px-2.5 py-1 rounded-lg text-xs font-bold hover:bg-emerald-700 transition flex items-center gap-1 shrink-0 shadow-2xs cursor-pointer"
                                            >
                                                <Edit size={12} />
                                                <span>Edit</span>
                                            </button>
                                        ) : (
                                            <div className="flex gap-1 shrink-0">
                                                <button
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setFormData({
                                                            name: user?.name || '',
                                                            email: user?.email || '',
                                                            phone: user?.phone || '',
                                                            bio: user?.bio || '',
                                                            image: user?.image || '',
                                                            location: user?.location || '',
                                                            dateOfBirth: user?.dateOfBirth || ''
                                                        });
                                                        setErrors({});
                                                    }}
                                                    className="border border-gray-300 text-gray-700 px-2 py-1 rounded-lg text-[11px] font-semibold hover:bg-gray-50 transition flex items-center gap-1"
                                                >
                                                    <X size={12} /> Cancel
                                                </button>
                                                <button
                                                    onClick={handleSaveProfile}
                                                    disabled={loading}
                                                    className="bg-emerald-600 text-white px-2.5 py-1 rounded-lg text-[11px] font-bold hover:bg-emerald-700 transition flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    <Save size={12} /> {loading ? 'Saving...' : 'Save'}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Form Fields */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                        <div>
                                            <label className="block text-[9px] font-bold text-gray-700 uppercase mb-0.5">Full Name *</label>
                                            <div className="relative">
                                                <User className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${isEditing ? 'text-emerald-600' : 'text-gray-400'}`} size={13} />
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        className={`w-full pl-7 pr-2 py-1 border rounded-lg text-xs focus:outline-none focus:border-emerald-600 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                                        placeholder="Full name"
                                                    />
                                                ) : (
                                                    <div className="w-full pl-7 pr-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-900 font-medium">
                                                        {formData.name || 'Not provided'}
                                                    </div>
                                                )}
                                            </div>
                                            {errors.name && <p className="mt-0.5 text-[9px] text-red-600 font-medium">{errors.name}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-[9px] font-bold text-gray-700 uppercase mb-0.5">Email Address *</label>
                                            <div className="relative">
                                                <Mail className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${isEditing ? 'text-emerald-600' : 'text-gray-400'}`} size={13} />
                                                {isEditing ? (
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        className={`w-full pl-7 pr-2 py-1 border rounded-lg text-xs focus:outline-none focus:border-emerald-600 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                                        placeholder="email@example.com"
                                                    />
                                                ) : (
                                                    <div className="w-full pl-7 pr-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-900 font-medium truncate">
                                                        {formData.email || 'Not provided'}
                                                    </div>
                                                )}
                                            </div>
                                            {errors.email && <p className="mt-0.5 text-[9px] text-red-600 font-medium">{errors.email}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-[9px] font-bold text-gray-700 uppercase mb-0.5">Mobile Number</label>
                                            <div className="relative">
                                                <Phone className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${isEditing ? 'text-emerald-600' : 'text-gray-400'}`} size={13} />
                                                {isEditing ? (
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleInputChange}
                                                        placeholder="Mobile number"
                                                        className={`w-full pl-7 pr-2 py-1 border rounded-lg text-xs focus:outline-none focus:border-emerald-600 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                                                    />
                                                ) : (
                                                    <div className="w-full pl-7 pr-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-900 font-medium">
                                                        {formData.phone || 'Not provided'}
                                                    </div>
                                                )}
                                            </div>
                                            {errors.phone && <p className="mt-0.5 text-[9px] text-red-600 font-medium">{errors.phone}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-[9px] font-bold text-gray-700 uppercase mb-0.5">Location / City</label>
                                            <div className="relative">
                                                <MapPin className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${isEditing ? 'text-emerald-600' : 'text-gray-400'}`} size={13} />
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        name="location"
                                                        value={formData.location}
                                                        onChange={handleInputChange}
                                                        placeholder="City, Country"
                                                        className="w-full pl-7 pr-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-emerald-600"
                                                    />
                                                ) : (
                                                    <div className="w-full pl-7 pr-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-900 font-medium">
                                                        {formData.location || 'Not provided'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[9px] font-bold text-gray-700 uppercase mb-0.5">Date of Birth</label>
                                            <div className="relative">
                                                <Calendar className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${isEditing ? 'text-emerald-600' : 'text-gray-400'}`} size={13} />
                                                {isEditing ? (
                                                    <input
                                                        type="date"
                                                        name="dateOfBirth"
                                                        value={formData.dateOfBirth}
                                                        onChange={handleInputChange}
                                                        className="w-full pl-7 pr-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-emerald-600"
                                                    />
                                                ) : (
                                                    <div className="w-full pl-7 pr-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-900 font-medium">
                                                        {formData.dateOfBirth || 'Not provided'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* About Me Bio */}
                                    <div className="pt-0.5">
                                        <label className="block text-[9px] font-bold text-gray-700 uppercase mb-0.5">About Me</label>
                                        {isEditing ? (
                                            <textarea
                                                name="bio"
                                                value={formData.bio}
                                                onChange={handleInputChange}
                                                rows="2"
                                                placeholder="Tell us about yourself..."
                                                className="w-full p-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-emerald-600"
                                            />
                                        ) : (
                                            <div className="w-full p-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 min-h-[45px] leading-relaxed">
                                                {formData.bio || "No bio provided yet. Click 'Edit' to add your summary."}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Settings Tab */}
                            {activeTab === 'settings' && (
                                <div className="space-y-2">
                                    <div className="border-b border-gray-100 pb-1.5">
                                        <h2 className="text-xs font-bold text-gray-900">Notification Settings</h2>
                                        <p className="text-[9px] text-gray-500">Configure email and push notification alerts</p>
                                    </div>

                                    <div className="space-y-1.5">
                                        {[
                                            { id: 'email', label: 'Email Notifications', desc: 'Receive course updates and announcements' },
                                            { id: 'push', label: 'Push Notifications', desc: 'Get instant alerts on your device' },
                                            { id: 'marketing', label: 'Promotional Offers', desc: 'Special discounts on new courses' },
                                            { id: 'newsletter', label: 'Weekly Digest', desc: 'Weekly learning insights & tips' }
                                        ].map(setting => (
                                            <div key={setting.id} className="flex items-center justify-between p-1.5 bg-gray-50/80 rounded-lg border border-gray-200/60">
                                                <div>
                                                    <h3 className="font-bold text-[11px] text-gray-900">{setting.label}</h3>
                                                    <p className="text-[8px] text-gray-500">{setting.desc}</p>
                                                </div>
                                                <label className="relative inline-block w-7 h-3.5 shrink-0 cursor-pointer">
                                                    <input type="checkbox" defaultChecked className="sr-only peer" />
                                                    <div className="w-full h-full bg-gray-300 rounded-full peer peer-checked:bg-emerald-600 transition"></div>
                                                    <div className="absolute left-0.5 top-0.5 bg-white w-2.5 h-2.5 rounded-full transition peer-checked:translate-x-3.5"></div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Security Tab */}
                            {activeTab === 'security' && (
                                <div className="space-y-2.5">
                                    <div className="border-b border-gray-100 pb-1.5">
                                        <h2 className="text-xs font-bold text-gray-900">Security & Privacy</h2>
                                        <p className="text-[9px] text-gray-500">Manage password and account protection</p>
                                    </div>

                                    <div className="p-2.5 bg-emerald-50/60 border border-emerald-200 rounded-lg space-y-1">
                                        <h3 className="font-bold text-[11px] text-gray-900">Change Password</h3>
                                        <p className="text-[9px] text-gray-600">Update your password regularly to keep your account safe.</p>
                                        <button 
                                            onClick={() => navigate('/')}
                                            className="bg-emerald-600 text-white px-2.5 py-1 rounded-lg text-[10px] font-bold hover:bg-emerald-700 transition"
                                        >
                                            Reset Password via OTP
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={() => setShowDeleteModal(false)}>
                    <div className="bg-white rounded-xl p-3.5 max-w-xs w-full text-center space-y-2.5" onClick={e => e.stopPropagation()}>
                        <div className="w-9 h-9 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                            <Trash2 size={18} />
                        </div>
                        <div>
                            <h2 className="text-xs font-bold text-gray-900">Delete Account?</h2>
                            <p className="text-[9px] text-gray-500 mt-0.5">This action cannot be undone. All your progress will be erased.</p>
                        </div>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="flex-1 bg-gray-200 text-gray-700 py-1 rounded-lg text-xs font-bold hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                className="flex-1 bg-red-600 text-white py-1 rounded-lg text-xs font-bold hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default Profile;